import { Currency } from "@redotech/money/currencies";
import { MoneyValue } from "@redotech/money/money-calculator";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { AdjustmentType, ReturnProductStrategy } from "../../return";

export interface ProductTotals<C extends Currency> {
  id: string;
  type: ReturnProductStrategy;
  /**
   * The return value for the product. This is the value that will be used in other return calculations,
   * all other product totals are used to calculate this value
   */
  returnValue: { amount: MoneyValue<C> };
  /** The new order value for the product. Will be zero unless the product has a variant exchange/exchange group */
  newOrderVariantValue: MoneyValue<C>;
  /** The adjustments applied to the product, including return flow and merchant manual adjustments */
  adjustments: { amount: MoneyValue<C>; type: AdjustmentType }[];
  originalValue: {
    /**
     * The discount for the product (generally just the line item discount divided by quantity, but can be influenced by
     * discount distribution, bundles, and returns of exchanges)
     */
    discount: MoneyValue<C>;
    /** The price after the discount has been applied */
    discountedPrice: MoneyValue<C>;
    price: MoneyValue<C>;
    tax: MoneyValue<C>;
    duty: MoneyValue<C>;
  };
}

export interface ReturnTotals<C extends Currency> {
  returnValue: {
    /** The total return value of all products being returned */
    amount: MoneyValue<C>;
  };
  /** A list of totals associated with each product, including the return value and all the values that were used to calculate it */
  productTotalsList: ProductTotals<C>[];
  /** The amount of store credit that will be issued to the customer */
  storeCredit: {
    withoutOriginalOrderShipping: MoneyValue<C>;
    withOriginalOrderShipping: MoneyValue<C>;
  };
  /** The amount of refund that will be issued to the customer */
  refund: {
    withoutOriginalOrderShipping: MoneyValue<C>;
    withOriginalOrderShipping: MoneyValue<C>;
  };
  /**
   * The amount of outstanding balance the customer owes, broken out by
   * whether the draft-billed exchange shipping fee is folded in.
   *
   * - `withoutDraftBilledShipping` — the pure return-time settle-up
   *   amount (charge minus refunds/credits, items + return-shipping
   *   only). Use for surfaces that itemize the exchange shipping line
   *   separately (e.g. a New Order breakdown that already shows the
   *   shipping line).
   * - `withDraftBilledShipping` — `withoutDraftBilledShipping` plus the
   *   exchange shipping fee when it's billed via the draft order
   *   (`chargesExchangeShippingViaStripe === false`); equal to
   *   `withoutDraftBilledShipping` otherwise. Use for surfaces showing
   *   a single aggregate "what you owe".
   *
   * Mirrors the `storeCredit.{with,without}OriginalOrderShipping` and
   * `refund.{with,without}OriginalOrderShipping` pattern so consumers
   * can pick the right total without re-deriving the billing path.
   */
  outstandingBalanceTotals: {
    withDraftBilledShipping: MoneyValue<C>;
    withoutDraftBilledShipping: MoneyValue<C>;
  };
  /**
   * @deprecated Use {@link outstandingBalanceTotals} instead. Equals
   * `outstandingBalanceTotals.withDraftBilledShipping`. Kept on the
   * output for one deploy cycle so frontend builds running ahead of (or
   * behind) the backend during a rolling deploy still resolve a value;
   * removed in a follow-up PR after both sides are caught up.
   */
  outstandingBalance: MoneyValue<C>;
  newOrderTotals: {
    /** The total price of all exchange items (post discounts and taxes) */
    totalValue: MoneyValue<C>;
    exchangeShippingFee: MoneyValue<C>;
  };
  adjustmentTotals: {
    /** Return level adjustments, such as finalize flow and customer label upcharge fee */
    returnLevelAdjustments: { amount: MoneyValue<C>; type: AdjustmentType }[];
    unusedUseItOrLoseItBonus: MoneyValue<C>;
  };
  /**
   * For returns with usePreDiscountPrice enabled, this is the sum of discounts across all return products
   * that will be applied as a use-it-or-lose-it shop now bonus
   */
  preDiscountShopNowBonus: {
    total: MoneyValue<C>;
    unused: MoneyValue<C>;
    used: MoneyValue<C>;
  };
  finalSaleCredit: MoneyValue<C>;
  returnShippingFee: {
    totalAmount: MoneyValue<C>;
    amountDeducted: {
      fromStoreCredit: MoneyValue<C>;
      fromRefund: MoneyValue<C>;
      // Optional for backwards compatibility — older serialized totals
      // (persisted snapshots, in-flight RPC payloads predating the
      // exchange-bucket deduction) won't have this field. Readers must
      // treat `undefined` as zero.
      fromExchange?: MoneyValue<C>;
    };
  };
}

interface ExchangeItemTotals<C extends Currency> {
  price: MoneyValue<C>;
  discount: MoneyValue<C>;
  discountedPrice: MoneyValue<C>;
}

export interface ProductSummaryTotals<
  C extends Currency,
> extends ProductTotals<C> {
  returnValue: ProductTotals<C>["returnValue"] & { preTax: MoneyValue<C> };
}

export interface ReturnSummaryTotals<C extends Currency> extends Omit<
  ReturnTotals<C>,
  "returnData"
> {
  presentmentCurrency: C;
  shopCurrency: Currency;
  returnValue: ReturnTotals<C>["returnValue"] & {
    preTaxPreAdjustments: MoneyValue<C>;
    preAdjustments: MoneyValue<C>;
    preReturnLevelAdjustments?: MoneyValue<C>;
    totalTaxes: MoneyValue<C>;
    totalDiscount: MoneyValue<C>;
    totalDuty: MoneyValue<C>;
    /**
     * Total duty (price + tax-on-duty) on returned line items eligible for a
     * merchant invoice credit (duty drawback). Only populated when the team
     * has duty drawback enabled and the return shipping country is in the
     * configured eligibility list. This is independent of `totalDuty`, which
     * tracks duties refunded to the customer.
     */
    merchantDutyDrawback: MoneyValue<C>;
  };
  totalPaymentOwed: MoneyValue<C>;
  newOrderTotals: ReturnTotals<C>["newOrderTotals"] & {
    preTax: MoneyValue<C>;
    exchangeItemTotalsList: ExchangeItemTotals<C>[];
    totalDiscount: MoneyValue<C>;
    taxes: {
      forVariantExchangeProducts: MoneyValue<C>;
      forAdvancedExchangeProducts: MoneyValue<C>;
      total: MoneyValue<C>;
    };
  };
  adjustmentTotals: ReturnTotals<C>["adjustmentTotals"] & {
    /** The net adjustment value after adding up all return and product level adjustments */
    totalNetAdjustmentValue: MoneyValue<C>;
    /** The net merchant manual adjustment value after adding up all merchant manual adjustments */
    totalNetMerchantManualAdjustment: MoneyValue<C>;
    totalCustomerLabelUpchargeFee: MoneyValue<C>;
    /** The net flow adjustment value after adding up all flow adjustments */
    totalFlowAdjustments: MoneyValue<C>;
    /** Sum of all positive (bonus) adjustments */
    totalBonuses: MoneyValue<C>;
    /** Sum of all negative (fee) adjustments */
    totalFees: MoneyValue<C>;
  };
  combinedUnusedUseItOrLoseItBonuses: MoneyValue<C>;
  originalOrderShippingFee: MoneyValue<C>;
  returnShippingFee: ReturnTotals<C>["returnShippingFee"] & {
    amountPaidWithCard: MoneyValue<C>;
    amountDeducted: ReturnTotals<C>["returnShippingFee"]["amountDeducted"] & {
      fromStoreCreditAndRefund: MoneyValue<C>;
    };
  };
  productTotalsList: ProductSummaryTotals<C>[];
}

export const ProductSummaryTotalsSchema_ = z.object({
  id: z.string(),
  type: createEnum(ReturnProductStrategy),
  returnValue: z.object({ amount: z.number(), preTax: z.number() }),
  newOrderVariantValue: z.number(),
  adjustments: z.array(
    z.object({ amount: z.number(), type: createEnum(AdjustmentType) }),
  ),
  originalValue: z.object({
    discount: z.number(),
    discountedPrice: z.number(),
    price: z.number(),
    tax: z.number(),
    duty: z.number(),
  }),
});

export const ProductSummaryTotalsSchema: z.ZodType<SerializedProductSummaryTotals> =
  ProductSummaryTotalsSchema_;
export type SerializedProductSummaryTotals = z.infer<
  typeof ProductSummaryTotalsSchema_
>;

export const ReturnSummaryTotalsSchema_ = z.object({
  presentmentCurrency: createEnum(Currency),
  shopCurrency: createEnum(Currency),
  productTotalsList: z.array(ProductSummaryTotalsSchema),
  storeCredit: z.object({
    withoutOriginalOrderShipping: z.number(),
    withOriginalOrderShipping: z.number(),
  }),
  refund: z.object({
    withoutOriginalOrderShipping: z.number(),
    withOriginalOrderShipping: z.number(),
  }),
  outstandingBalanceTotals: z
    .object({
      withDraftBilledShipping: z.number(),
      withoutDraftBilledShipping: z.number(),
    })
    .nullish(),
  /** @deprecated mirrors `outstandingBalanceTotals.withDraftBilledShipping`. */
  outstandingBalance: z.number(),
  totalPaymentOwed: z.number(),
  newOrderTotals: z.object({
    totalValue: z.number(),
    preTax: z.number(),
    totalDiscount: z.number(),
    exchangeItemTotalsList: z.array(
      z.object({
        price: z.number(),
        discount: z.number(),
        discountedPrice: z.number(),
      }),
    ),
    taxes: z.object({
      forVariantExchangeProducts: z.number(),
      forAdvancedExchangeProducts: z.number(),
      total: z.number(),
    }),
    exchangeShippingFee: z.number(),
  }),
  originalOrderShippingFee: z.number(),
  returnShippingFee: z.object({
    totalAmount: z.number(),
    amountPaidWithCard: z.number(),
    amountDeducted: z.object({
      fromStoreCredit: z.number(),
      fromRefund: z.number(),
      fromStoreCreditAndRefund: z.number(),
      // Optional for backwards compatibility — see comment on
      // ReturnTotals.returnShippingFee.amountDeducted.fromExchange.
      fromExchange: z.number().optional(),
    }),
  }),
  returnValue: z.object({
    amount: z.number(),
    preTaxPreAdjustments: z.number(),
    preReturnLevelAdjustments: z.number().optional(),
    totalTaxes: z.number(),
    totalDiscount: z.number(),
    totalDuty: z.number(),
    merchantDutyDrawback: z.number().optional(),
  }),
  preDiscountShopNowBonus: z.object({
    total: z.number(),
    unused: z.number(),
    used: z.number(),
  }),
  finalSaleCredit: z.number(),
  adjustmentTotals: z.object({
    returnLevelAdjustments: z.array(
      z.object({ amount: z.number(), type: createEnum(AdjustmentType) }),
    ),
    totalCustomerLabelUpchargeFee: z.number(),
    totalNetAdjustmentValue: z.number(),
    totalNetMerchantManualAdjustment: z.number(),
    totalFlowAdjustments: z.number().nullish(),
    totalBonuses: z.number().nullish(),
    totalFees: z.number().nullish(),
  }),
});

export const ReturnSummaryTotalsSchema: z.ZodType<SerializedReturnSummaryTotals> =
  ReturnSummaryTotalsSchema_;
export type SerializedReturnSummaryTotals = z.infer<
  typeof ReturnSummaryTotalsSchema_
>;
